import { useState } from "react";
import { Phone } from "lucide-react";

const services = [
    {
        title: "Non-Emergency Medical Transportation",
        desc: "Dependable transportation for medical appointments, treatments, therapy sessions, and discharge rides.",
        icon: "🩺",
    },
    {
        title: "Wheelchair Transportation",
        desc: "Safe and respectful wheelchair-accessible transportation with comfort and care at the center of every trip.",
        icon: "♿",
    },
    {
        title: "Scheduled Rides",
        desc: "Pre-booked transportation for recurring appointments, dialysis, rehabilitation, and specialty care visits.",
        icon: "📅",
    },
    {
        title: "Senior Transportation",
        desc: "Compassionate transportation support for seniors who need reliable rides to healthcare and essential appointments.",
        icon: "❤️",
    },
];

const highlights = [
    "Professional, courteous service",
    "Clean, comfortable vehicles",
    "On-time pickup coordination",
    "Patient-centered transportation experience",
    "Dependable communication",
    "Built for healthcare, family, and facility confidence",
];

const faqs = [
    {
        q: "What areas do you serve?",
        a: "Precision Care Transport is based in North Carolina and can be positioned to serve Charlotte and surrounding communities. You can customize this section with your exact counties and coverage radius before publishing.",
    },
    {
        q: "Do you offer recurring transportation?",
        a: "Yes. The site includes messaging for recurring appointments such as dialysis, rehab, therapy, and routine medical visits.",
    },
    {
        q: "Can facilities or case managers request service?",
        a: "Yes. The layout is built so hospitals, clinics, discharge coordinators, social workers, and family members can quickly understand your value and request transportation.",
    },
    {
        q: "How do clients request a ride?",
        a: "Clients can use the contact/request form section on this site. You can also call, email, or text us directly and we'll get you scheduled.",
    },
];

function BookRideForm() {
    const [formStatus, setFormStatus] = useState("idle");
    const inputCls =
        "rounded-2xl border border-white/10 bg-neutral-900 px-4 py-3 outline-none placeholder:text-white/35 focus:border-red-500/50 transition text-white";

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormStatus("submitting");
        const form = e.currentTarget;
        const formData = new FormData(form);
        try {
            const response = await fetch(form.action, {
                method: "POST",
                body: formData,
                headers: { Accept: "application/json" },
            });
            if (response.ok) {
                setFormStatus("success");
                form.reset();
            } else {
                setFormStatus("error");
            }
        } catch (error) {
            setFormStatus("error");
        }
    };

    return (
        <div className="rounded-[2rem] border border-white/10 bg-white/5 p-8">
            <h4 className="text-2xl font-semibold">Request Transportation</h4>
            <p className="mt-2 text-sm text-white/60">
                Send a ride request and our team will confirm details by email
                or phone.
            </p>
            <form
                action="https://formsubmit.co/ajax/info@precisioncaretransport.com"
                method="POST"
                data-testid="book-form"
                onSubmit={handleSubmit}
                className="mt-6 grid gap-4"
            >
                {/* Honeypot (spam trap) */}
                <input
                    type="text"
                    name="_honey"
                    tabIndex="-1"
                    autoComplete="off"
                    style={{ display: "none" }}
                />
                <input
                    type="hidden"
                    name="_subject"
                    value="New Transportation Request - Precision Care Transport"
                />
                <input type="hidden" name="_template" value="table" />
                <input type="hidden" name="_captcha" value="false" />
                <input
                    type="hidden"
                    name="_autoresponse"
                    value="Thank you for contacting Precision Care Transport. We received your request and will contact you shortly."
                />
                <input
                    type="hidden"
                    name="_webhook"
                    value="https://hooks.zapier.com/hooks/catch/27332451/ujxhu62/"
                />

                <input
                    data-testid="input-fullName"
                    name="Full Name"
                    required
                    className={inputCls}
                    placeholder="Full Name"
                />
                <input
                    data-testid="input-phone"
                    name="Phone Number"
                    type="tel"
                    required
                    className={inputCls}
                    placeholder="Phone Number"
                />
                <input
                    data-testid="input-email"
                    name="Email"
                    type="email"
                    required
                    className={inputCls}
                    placeholder="Email Address"
                />
                <input
                    data-testid="input-pickup"
                    name="Pickup Address"
                    required
                    className={inputCls}
                    placeholder="Pickup / Appointment Area"
                />
                <textarea
                    data-testid="input-notes"
                    name="Notes"
                    className={
                        inputCls + " min-h-[150px] resize-y leading-relaxed"
                    }
                    placeholder="Describe the transportation need"
                />
                <button
                    type="submit"
                    data-testid="submit-book-btn"
                    disabled={formStatus === "submitting"}
                    className="mt-2 inline-flex items-center justify-center gap-2 rounded-2xl bg-red-600 px-6 py-3 text-base font-semibold shadow-xl shadow-red-950/30 transition hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-70"
                >
                    {formStatus === "submitting"
                        ? "Submitting..."
                        : "Submit Request"}
                </button>

                {formStatus === "success" && (
                    <div
                        data-testid="form-success"
                        className="rounded-2xl border border-green-500/30 bg-green-500/10 px-4 py-3 text-sm text-green-300"
                    >
                        Thank you. Your transportation request has been
                        received. We will contact you shortly.
                    </div>
                )}

                {formStatus === "error" && (
                    <div
                        data-testid="form-error"
                        className="rounded-2xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300"
                    >
                        Something went wrong while submitting your request.
                        Please try again, or call 980-925-4006.
                    </div>
                )}

                <p className="text-xs text-white/45">
                    Submissions go directly to info@precisioncaretransport.com.
                    You'll receive an auto-confirmation email.
                </p>
            </form>
        </div>
    );
}

export default function Home() {
    return (
        <div
            data-testid="home-page"
            className="min-h-screen bg-neutral-950 text-white"
        >
            <header className="sticky top-0 z-50 border-b border-white/10 bg-neutral-950/85 backdrop-blur">
                <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
                    <a
                        href="#top"
                        data-testid="header-logo-link"
                        className="flex items-center gap-3"
                    >
                        <img
                            src="/precisionlogo.png"
                            alt="Precision Care Transport"
                            className="h-14 w-14 object-contain"
                        />
                        <span className="hidden sm:block">
                            <p className="text-xs uppercase tracking-[0.35em] text-red-400">
                                Precision Care Transport
                            </p>
                            <h1 className="text-lg font-semibold">
                                Elite Medical Transportation
                            </h1>
                        </span>
                    </a>
                    <div className="hidden gap-6 text-sm text-white/80 md:flex">
                        <a
                            href="#services"
                            data-testid="nav-services"
                            className="transition hover:text-white"
                        >
                            Services
                        </a>
                        <a
                            href="#about"
                            data-testid="nav-about"
                            className="transition hover:text-white"
                        >
                            About
                        </a>
                        <a
                            href="#coverage"
                            data-testid="nav-coverage"
                            className="transition hover:text-white"
                        >
                            Coverage
                        </a>
                        <a
                            href="#faq"
                            data-testid="nav-faq"
                            className="transition hover:text-white"
                        >
                            FAQ
                        </a>
                        <a
                            href="#contact"
                            data-testid="nav-contact"
                            className="transition hover:text-white"
                        >
                            Contact
                        </a>
                    </div>
                    <a
                        href="#contact"
                        data-testid="header-cta"
                        className="rounded-2xl border border-red-500/40 bg-red-600 px-4 py-2 text-sm font-medium shadow-lg shadow-red-900/30 transition hover:bg-red-500"
                    >
                        Request Transportation
                    </a>
                </div>
            </header>

            <section className="relative overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(220,38,38,0.24),transparent_28%),radial-gradient(circle_at_bottom_left,rgba(255,255,255,0.06),transparent_22%)]" />
                <div className="relative mx-auto grid max-w-7xl gap-10 px-6 py-20 md:grid-cols-2 md:items-center md:py-28">
                    <div>
                        <p className="mb-4 inline-flex rounded-full border border-red-500/30 bg-red-500/10 px-4 py-1 text-sm text-red-300">
                            Professional • Reliable • Compassionate
                        </p>
                        <h2 className="max-w-2xl text-4xl font-bold leading-tight md:text-6xl">
                            Safe, dependable transportation with dignity and
                            care.
                        </h2>
                        <p className="mt-6 max-w-xl text-lg leading-8 text-white/75">
                            Precision Care Transport provides premium
                            non-emergency medical transportation for clients,
                            families, healthcare providers, and facilities that
                            need reliability, professionalism, and a
                            patient-first experience.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-4">
                            <a
                                href="#contact"
                                data-testid="hero-book-cta"
                                className="rounded-2xl bg-red-600 px-6 py-3 text-base font-semibold shadow-xl shadow-red-950/40 transition hover:bg-red-500"
                            >
                                Book a Ride
                            </a>
                            <a
                                href="#services"
                                data-testid="hero-services-cta"
                                className="rounded-2xl border border-white/15 bg-white/5 px-6 py-3 text-base font-semibold transition hover:bg-white/10"
                            >
                                Explore Services
                            </a>
                        </div>
                        <div className="mt-10 grid max-w-xl grid-cols-1 gap-4 sm:grid-cols-3">
                            <div className="rounded-3xl border border-white/10 bg-white/5 p-4">
                                <p className="text-2xl font-bold">
                                    Professional
                                </p>
                                <p className="mt-1 text-sm text-white/65">
                                    Built to inspire confidence with clients
                                    and facilities.
                                </p>
                            </div>
                            <div className="rounded-3xl border border-white/10 bg-white/5 p-4">
                                <p className="text-2xl font-bold">
                                    Patient-Centered
                                </p>
                                <p className="mt-1 text-sm text-white/65">
                                    Focused on comfort, care, and dependable
                                    transport.
                                </p>
                            </div>
                            <div className="rounded-3xl border border-white/10 bg-white/5 p-4">
                                <p className="text-2xl font-bold">Reliable</p>
                                <p className="mt-1 text-sm text-white/65">
                                    Transportation support for essential
                                    appointments and needs.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="relative">
                        <div className="rounded-[2rem] border border-white/10 bg-gradient-to-br from-white/10 to-white/5 p-6 shadow-2xl shadow-black/40 backdrop-blur">
                            <div className="rounded-[1.5rem] border border-red-500/20 bg-neutral-900 p-8">
                                <div className="mb-6 flex items-center justify-between">
                                    <div>
                                        <p className="text-sm uppercase tracking-[0.3em] text-red-400">
                                            Trusted Ride Support
                                        </p>
                                        <h3 className="mt-2 text-2xl font-semibold">
                                            Transportation Request
                                        </h3>
                                    </div>
                                    <div className="rounded-2xl bg-red-600/20 px-3 py-2 text-xl">
                                        🚐
                                    </div>
                                </div>
                                <div className="space-y-4 text-sm text-white/80">
                                    <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                                        Medical appointments, rehabilitation,
                                        treatments, discharge pickup, and
                                        scheduled care visits.
                                    </div>
                                    <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                                        Designed to communicate credibility to
                                        families, facilities, social workers,
                                        and case managers.
                                    </div>
                                    <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                                        Submit the form below to send a ride
                                        inquiry straight to
                                        info@precisioncaretransport.com.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section
                id="services"
                data-testid="services-section"
                className="mx-auto max-w-7xl px-6 py-20"
            >
                <div className="mb-12 max-w-2xl">
                    <p className="text-sm uppercase tracking-[0.3em] text-red-400">
                        Services
                    </p>
                    <h3 className="mt-3 text-3xl font-bold md:text-4xl">
                        Transportation services built around reliability and
                        care
                    </h3>
                </div>
                <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
                    {services.map((service, i) => (
                        <div
                            key={service.title}
                            data-testid={`service-${i}`}
                            className="rounded-[1.75rem] border border-white/10 bg-white/5 p-6 shadow-lg shadow-black/20 transition hover:-translate-y-1 hover:bg-white/10"
                        >
                            <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-red-600/20 text-2xl">
                                {service.icon}
                            </div>
                            <h4 className="text-xl font-semibold">
                                {service.title}
                            </h4>
                            <p className="mt-3 leading-7 text-white/70">
                                {service.desc}
                            </p>
                        </div>
                    ))}
                </div>
            </section>

            <section
                id="about"
                data-testid="about-section"
                className="border-y border-white/10 bg-white/[0.03]"
            >
                <div className="mx-auto grid max-w-7xl gap-10 px-6 py-20 md:grid-cols-2 md:items-center">
                    <div>
                        <p className="text-sm uppercase tracking-[0.3em] text-red-400">
                            About
                        </p>
                        <h3 className="mt-3 text-3xl font-bold md:text-4xl">
                            A transportation brand that communicates trust from
                            the first click
                        </h3>
                        <p className="mt-5 text-lg leading-8 text-white/75">
                            Precision Care Transport is a professional North
                            Carolina transportation business centered on safety,
                            timeliness, and compassionate service.
                        </p>
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                        {highlights.map((item) => (
                            <div
                                key={item}
                                className="rounded-3xl border border-white/10 bg-neutral-900 p-5"
                            >
                                <p className="text-base font-medium">{item}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section
                id="coverage"
                data-testid="coverage-section"
                className="mx-auto max-w-7xl px-6 py-20"
            >
                <div className="grid gap-8 md:grid-cols-[1.1fr_0.9fr]">
                    <div className="rounded-[2rem] border border-white/10 bg-white/5 p-8">
                        <p className="text-sm uppercase tracking-[0.3em] text-red-400">
                            Coverage Area
                        </p>
                        <h3 className="mt-3 text-3xl font-bold">
                            Serving the Greater Charlotte area
                        </h3>
                        <p className="mt-5 max-w-2xl leading-8 text-white/75">
                            Precision Care Transport proudly serves clients,
                            families, healthcare providers, and facilities
                            throughout the Greater Charlotte region. Coverage
                            details for specific counties and service zones
                            are available upon request — please contact
                            dispatch to confirm availability for your pickup
                            and drop-off locations.
                        </p>
                        <a
                            href="#contact"
                            data-testid="coverage-cta"
                            className="mt-7 inline-flex items-center rounded-2xl border border-white/15 bg-white/5 px-5 py-2.5 text-sm font-semibold transition hover:bg-white/10"
                        >
                            Request coverage details
                        </a>
                    </div>
                    <div
                        data-testid="trust-section"
                        className="rounded-[2rem] border border-red-500/20 bg-gradient-to-br from-red-950/40 to-neutral-900 p-8"
                    >
                        <p className="text-sm uppercase tracking-[0.3em] text-red-400">
                            Why Trust Us
                        </p>
                        <h4 className="mt-3 text-2xl font-semibold">
                            Professional, insured, patient-centered care
                        </h4>
                        <ul className="mt-5 space-y-3 text-white/80">
                            <li className="flex gap-3">
                                <span className="mt-2 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-red-400" />
                                <span>
                                    Fully insured commercial transportation
                                    provider
                                </span>
                            </li>
                            <li className="flex gap-3">
                                <span className="mt-2 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-red-400" />
                                <span>
                                    Licensed in accordance with applicable
                                    North Carolina requirements
                                </span>
                            </li>
                            <li className="flex gap-3">
                                <span className="mt-2 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-red-400" />
                                <span>
                                    Professional, safety-focused drivers
                                </span>
                            </li>
                            <li className="flex gap-3">
                                <span className="mt-2 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-red-400" />
                                <span>
                                    Reliable, patient-centered transportation
                                    service
                                </span>
                            </li>
                            <li className="flex gap-3">
                                <span className="mt-2 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-red-400" />
                                <span>
                                    Coverage details available upon request
                                </span>
                            </li>
                        </ul>
                    </div>
                </div>
            </section>

            <section
                id="faq"
                data-testid="faq-section"
                className="border-y border-white/10 bg-white/[0.03]"
            >
                <div className="mx-auto max-w-7xl px-6 py-20">
                    <div className="mb-10 max-w-2xl">
                        <p className="text-sm uppercase tracking-[0.3em] text-red-400">
                            FAQ
                        </p>
                        <h3 className="mt-3 text-3xl font-bold md:text-4xl">
                            Common questions
                        </h3>
                    </div>
                    <div className="grid gap-5 md:grid-cols-2">
                        {faqs.map((item) => (
                            <div
                                key={item.q}
                                className="rounded-[1.75rem] border border-white/10 bg-neutral-900 p-6"
                            >
                                <h4 className="text-lg font-semibold">
                                    {item.q}
                                </h4>
                                <p className="mt-3 leading-7 text-white/70">
                                    {item.a}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section
                id="contact"
                data-testid="contact-section"
                className="mx-auto max-w-7xl px-6 py-20"
            >
                <div className="grid gap-8 md:grid-cols-[0.95fr_1.05fr]">
                    <div className="rounded-[2rem] border border-white/10 bg-gradient-to-br from-red-950/30 to-neutral-900 p-8">
                        <p className="text-sm uppercase tracking-[0.3em] text-red-400">
                            Contact
                        </p>
                        <h3 className="mt-3 text-3xl font-bold">
                            Ready to book your ride with Precision Care
                            Transport?
                        </h3>
                        <p className="mt-5 leading-8 text-white/75">
                            Use the form to send a ride request directly to
                            our dispatch team. We'll confirm your details by
                            email or phone.
                        </p>
                        <div className="mt-8 space-y-4 text-white/75">
                            <p>
                                <span className="font-semibold text-white">
                                    Phone:
                                </span>{" "}
                                <a
                                    data-testid="contact-phone"
                                    href="tel:+19809254006"
                                    className="underline decoration-red-500/60 hover:text-white"
                                >
                                    980-925-4006
                                </a>
                            </p>
                            <p>
                                <span className="font-semibold text-white">
                                    Email:
                                </span>{" "}
                                <a
                                    href="mailto:info@precisioncaretransport.com"
                                    className="underline decoration-red-500/60 hover:text-white"
                                >
                                    info@precisioncaretransport.com
                                </a>
                            </p>
                            <div>
                                <p className="font-semibold text-white">
                                    Hours:
                                </p>
                                <ul className="mt-2 space-y-1 text-white/75">
                                    <li>Monday – Friday: 5:00 AM – 7:00 PM</li>
                                    <li>Saturday: 7:00 AM – 3:00 PM</li>
                                    <li>
                                        Sunday: By Appointment / Priority
                                        Discharge Only
                                    </li>
                                </ul>
                            </div>
                            <p>
                                <span className="font-semibold text-white">
                                    Location:
                                </span>{" "}
                                Serving Charlotte and surrounding communities
                            </p>
                        </div>
                    </div>

                    <BookRideForm />
                </div>
            </section>

            <footer className="border-t border-white/10 bg-neutral-950">
                <div className="mx-auto flex max-w-7xl flex-col gap-4 px-6 py-8 text-sm text-white/55 md:flex-row md:items-center md:justify-between">
                    <p>
                        &copy; {new Date().getFullYear()} Precision Care
                        Transport. All rights reserved.
                    </p>
                    <p>
                        Built for a premium, professional launch presence.
                    </p>
                </div>
            </footer>

            <a
                href="tel:+19809254006"
                data-testid="floating-call-btn"
                aria-label="Tap to call 980-925-4006"
                className="md:hidden fixed bottom-5 right-5 z-50 inline-flex items-center gap-2.5 rounded-full bg-red-600 px-5 py-3.5 text-sm font-semibold text-white shadow-2xl shadow-red-900/50 ring-1 ring-red-400/40 hover:bg-red-500 active:scale-95 transition"
            >
                <span className="relative flex h-3 w-3 items-center justify-center">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-300 opacity-75" />
                    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-white" />
                </span>
                <Phone className="h-4 w-4" />
                Tap to call 980-925-4006
            </a>
        </div>
    );
}
